import { createFileRoute, Link } from "@tanstack/react-router";
import { Mail } from "lucide-react";
import { SiteShell } from "@/components/site/SiteShell";
import { useLang } from "@/hooks/use-language";

export const Route = createFileRoute("/founder")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://jozori.com/founder" },
      { title: "Meet the Founder | تعرّف على المؤسس — Jozori" },
      {
        name: "description",
        content:
          "Bilal Almadani, founder of Jozori — helping children stay connected to their language, values, and identity through a warm, thoughtful digital world.",
      },
      { property: "og:title", content: "Meet the Founder | تعرّف على المؤسس" },
      {
        property: "og:description",
        content:
          "Bilal Almadani, founder of Jozori — a warm digital world where children learn, grow, and build confidence through stories, values, and language.",
      },

    ],
    links: [{ rel: "canonical", href: "https://jozori.com/founder" }],
  }),
  component: FounderPage,
});

const COPY = {
  en: {
    eyebrow: "Meet the Founder",
    name: "Bilal Almadani",
    role: "Founder, Jozori",
    paragraphs: [
      "Bilal Almadani holds a Master's degree in Marketing and a background in Business Administration, with experience in brand development, digital ventures, and building ideas that connect technology with meaningful human experiences.",
      "Living and working across different countries gave him a close understanding of a challenge shared by many Arab families: helping children stay connected to their language, values, identity, and sense of belonging while growing up in a rapidly changing digital world.",
      "From this understanding, Jozori was created — not simply as an educational app, but as a warm digital world where children can learn, grow, and build confidence through stories, values, language, and meaningful everyday experiences.",
      "Through Jozori, his goal is to help children grow with a stronger connection to their identity, family, culture, and values — while giving parents a thoughtful digital experience they can feel good about bringing into their children's lives.",
    ],
    ctaContact: "Contact our team",
  },
  ar: {
    eyebrow: "تعرّف على المؤسس",
    name: "بلال المداني",
    role: "مؤسس جذوري",
    paragraphs: [
      "يحمل بلال المداني درجة الماجستير في التسويق، ولديه خلفية أكاديمية في إدارة الأعمال، إلى جانب خبرة في تطوير العلامات التجارية والمشاريع الرقمية وبناء أفكار تجمع بين التكنولوجيا والتجارب الإنسانية الهادفة.",
      "أتاحت له تجربة العيش والعمل في بلدان مختلفة فهمًا قريبًا لتحدٍّ تشترك فيه الكثير من العائلات العربية: كيف نساعد أطفالنا على البقاء على صلة بلغتهم وقيمهم وهويتهم وشعورهم بالانتماء، وهم يكبرون في عالم رقمي سريع التغيّر.",
      "من هذا الفهم وُلدت جذوري — ليس كتطبيق تعليمي فحسب، بل كعالم رقمي دافئ يستطيع فيه الطفل أن يتعلّم وينمو ويبني ثقته بنفسه من خلال القصص والقيم واللغة والتجارب اليومية الهادفة.",
      "ومن خلال جذوري، يهدف بلال إلى مساعدة الأطفال على النمو وهم أكثر ارتباطًا بهويتهم وعائلاتهم وثقافتهم وقيمهم، وفي الوقت نفسه تقديم تجربة رقمية مدروسة يشعر الأهل بالاطمئنان وهم يجعلونها جزءًا من حياة أطفالهم.",
    ],
    ctaContact: "تواصل مع فريقنا",
  },
} as const;


function FounderPage() {
  const { lang } = useLang();
  const t = COPY[lang];
  const isAr = lang === "ar";
  return (
    <SiteShell active="/founder">
      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/[0.04] to-transparent"
        />
        {/* Monogram watermark — softer, more subtle */}
        <div
          aria-hidden="true"
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 select-none pointer-events-none"
        >
          <span
            className="block text-[14rem] md:text-[22rem] font-black leading-none tracking-tighter"
            style={{
              background:
                "linear-gradient(135deg, rgba(200,122,74,0.06), rgba(16,185,129,0.03))",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {isAr ? "ج" : "J"}
          </span>
        </div>

        <div className="relative mx-auto max-w-2xl px-4 py-16 md:py-20 text-center">
          <p className="mb-5 text-[10px] font-bold uppercase tracking-[0.28em] text-primary/80">
            {t.eyebrow}
          </p>
          <h1
            className={`text-2xl md:text-4xl font-bold tracking-tight mb-2 ${
              isAr ? "font-display-ar leading-snug" : "font-display-en leading-tight"
            }`}
          >
            {t.name}
          </h1>
          <p className="text-sm font-medium text-foreground/55 tracking-wide mb-10">
            {t.role}
          </p>
          <div className="mx-auto mb-10 h-px w-12 bg-primary/30" />

          <div
            className={`space-y-5 text-[15px] md:text-base text-foreground/75 max-w-[62ch] mx-auto ${
              isAr ? "text-right leading-[2] font-display-ar" : "text-left md:text-center leading-[1.8]"
            }`}
          >
            {t.paragraphs.map((p, i) => (
              <p key={i} className={i === 0 ? "text-foreground/90 font-medium" : ""}>
                {p}
              </p>
            ))}
          </div>



          {/* Only official channels — no personal founder contact */}
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <a
              href="mailto:info@jozori.com"
              className="inline-flex items-center gap-2 rounded-full border border-clay/40 bg-card px-5 py-2.5 text-sm font-semibold hover:bg-clay/20 transition"
            >
              <Mail className="size-4" aria-hidden="true" /> <span>info@jozori.com</span>
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition"
            >
              {t.ctaContact}
            </Link>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
